import { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { ConfirmDialog } from '@core/components/ui';

/**
 * BaseDeleteModal Component
 *
 * Confirmation modal for deleting a dedicated knowledge base.
 * Uses ConfirmDialog wrapper for consistent delete pattern.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onCancel - Callback when modal is closed/cancelled
 * @param {Function} props.onConfirm - Callback when delete is confirmed
 * @param {Object} props.base - Knowledge base object to delete
 */
export function BaseDeleteModal({ isOpen, onCancel, onConfirm, base }) {
  const { t } = useTranslation();

  const handleConfirm = useCallback(async () => {
    if (!base) return;
    await onConfirm(base.id);
  }, [base, onConfirm]);

  if (!base) return null;

  return (
    <ConfirmDialog
      open={isOpen}
      onClose={onCancel}
      onConfirm={handleConfirm}
      title={t('knowledgeBases.delete.title', 'Delete Knowledge Base')}
      message={t(
        'knowledgeBases.delete.message',
        `Are you sure you want to delete "${base.name}"? This action cannot be undone.`
      )}
      confirmText={t('common.delete', 'Delete')}
      cancelText={t('common.cancel', 'Cancel')}
      variant="danger"
    />
  );
}

BaseDeleteModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  base: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
  }),
};
